import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiRoutes } from './ApiRoutes';
import { Gt2ApiService } from './gt2-api.service';
import {
  BellNotificationChange,
  BellNotificationFilter,
  BellNotificationResponse,
  BellNotificationsListResponse,
  BellReadAllResponse,
  BellUnreadCountResponse,
  NotificationPreferenceChoice,
  NotificationPreferencesResponse,
  NotificationSettingModel,
  NotificationSettingsResponse,
  NotificationSettingUpdate,
} from './models/NotificationCenterModel';

/**
 * Notification center (Centre de notifications) — bell of the toolbar,
 * preferences of the user and settings of the organization (backend
 * container NotificationCenter: /v1/bell-notifications,
 * /v1/notification-preferences, /v1/notification-settings).
 * Offered to every plan: no module gate. The organization settings are
 * reserved to GOD, OWNER and ADMIN by the API (403 otherwise).
 */
@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
  constructor(
    private http: HttpClient,
    private api: Gt2ApiService,
  ) {}

  // ---- Bell ----

  public getBellNotifications(
    state: BellNotificationFilter = 'ACTIVE',
    page: number = 1,
    limit: number = 20,
  ): Observable<BellNotificationsListResponse> {
    const params = new HttpParams()
      .set('state', state)
      .set('page', String(page))
      .set('limit', String(limit));
    return this.http.get<BellNotificationsListResponse>(
      this.api.createUrl(ApiRoutes.BELL_NOTIFICATIONS),
      { params },
    );
  }

  public getUnreadCount(): Observable<BellUnreadCountResponse> {
    return this.http.get<BellUnreadCountResponse>(
      this.api.createUrl(ApiRoutes.BELL_NOTIFICATIONS_UNREAD_COUNT),
    );
  }

  public readAll(): Observable<BellReadAllResponse> {
    return this.http.post<BellReadAllResponse>(
      this.api.createUrl(ApiRoutes.BELL_NOTIFICATIONS_READ_ALL),
      {},
    );
  }

  /** read | unread | archive | unarchive */
  public changeState(
    uuid: string,
    change: BellNotificationChange,
  ): Observable<BellNotificationResponse> {
    return this.http.post<BellNotificationResponse>(
      this.api.createUrl(ApiRoutes.BELL_NOTIFICATION) +
        encodeURIComponent(uuid) +
        '/' +
        change,
      {},
    );
  }

  // ---- Preferences of the user ----

  public getPreferences(): Observable<NotificationPreferencesResponse> {
    return this.http.get<NotificationPreferencesResponse>(
      this.api.createUrl(ApiRoutes.NOTIFICATION_PREFERENCES),
    );
  }

  public updatePreferences(
    preferences: NotificationPreferenceChoice[],
  ): Observable<NotificationPreferencesResponse> {
    return this.http.put<NotificationPreferencesResponse>(
      this.api.createUrl(ApiRoutes.NOTIFICATION_PREFERENCES),
      { preferences },
    );
  }

  // ---- Settings of the organization (GOD, OWNER, ADMIN) ----

  public getOrganizationSettings(): Observable<NotificationSettingsResponse> {
    return this.http.get<NotificationSettingsResponse>(
      this.api.createUrl(ApiRoutes.NOTIFICATION_SETTINGS),
    );
  }

  public updateOrganizationSetting(
    key: string,
    changes: NotificationSettingUpdate,
  ): Observable<{ data: NotificationSettingModel }> {
    return this.http.put<{ data: NotificationSettingModel }>(
      this.api.createUrl(ApiRoutes.NOTIFICATION_SETTING) +
        encodeURIComponent(key),
      changes,
    );
  }

  public resetOrganizationSetting(
    key: string,
  ): Observable<{ data: NotificationSettingModel }> {
    return this.http.delete<{ data: NotificationSettingModel }>(
      this.api.createUrl(ApiRoutes.NOTIFICATION_SETTING) +
        encodeURIComponent(key),
    );
  }
}
